import type { FindingQueryState } from "../../api/types.js";
import { DEFAULT_FINDING_QUERY } from "../findings/model.js";
import type { InventorySbomRow, InventorySelection } from "./inventory-selection.js";

const AFFECTED_FINDINGS_PAGE_SIZE = 25;

/** Same encoding InventoryPage uses for `componentFindingsHref`. */
export function base64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

export function encodeInventoryComponentKey(component: InventorySbomRow): string {
  return base64Url(
    JSON.stringify({
      ecosystem: component.ecosystem,
      name: component.name,
      version: component.version || null,
    }),
  );
}

export function affectedFindingsQuery(selection: InventorySelection): FindingQueryState | null {
  if (selection.kind === "sbom") {
    return {
      ...DEFAULT_FINDING_QUERY,
      statuses: [],
      preset: "all",
      analysisFamilies: ["sca", "container"],
      componentKeys: [encodeInventoryComponentKey(selection.row)],
      scanId: selection.row.scanId,
      page: 1,
      pageSize: AFFECTED_FINDINGS_PAGE_SIZE,
    };
  }
  if (!selection.row.findingRuleId) return null;
  return {
    ...DEFAULT_FINDING_QUERY,
    statuses: [],
    preset: "all",
    analysisFamilies: ["pqc"],
    ruleIds: [selection.row.findingRuleId],
    scanId: selection.row.scanId,
    page: 1,
    pageSize: AFFECTED_FINDINGS_PAGE_SIZE,
  };
}

/** Compact findings-page search matching InventoryPage related-findings hrefs. */
export function inventoryFindingsSearch(selection: InventorySelection): string | null {
  if (selection.kind === "sbom") {
    const params = new URLSearchParams({
      statuses: "",
      preset: "all",
      analysisFamilies: "sca,container",
      componentKeys: encodeInventoryComponentKey(selection.row),
      scanId: selection.row.scanId,
    });
    return `?${params.toString()}`;
  }
  if (!selection.row.findingRuleId) return null;
  const params = new URLSearchParams({
    statuses: "",
    preset: "all",
    analysisFamilies: "pqc",
    ruleIds: selection.row.findingRuleId,
    scanId: selection.row.scanId,
  });
  return `?${params.toString()}`;
}

export function affectedFindingHref(
  selection: InventorySelection,
  findingId: string,
): string | null {
  const search = inventoryFindingsSearch(selection);
  if (!search) return null;
  return `/findings${search}#${findingId}`;
}

export function sbomComponentFindingsHref(component: InventorySbomRow): string {
  return `/findings${inventoryFindingsSearch({ kind: "sbom", row: component }) ?? ""}`;
}
